/**
 * AxLabs Experiments - Circular Motion and Centripetal Force
 *
 * Demonstrates uniform circular motion and centripetal acceleration.
 *
 * Governing equations:
 * - v = ωr (tangential velocity)
 * - a_c = v²/r = ω²r (centripetal acceleration)
 * - F_c = mv²/r = mω²r (centripetal force)
 * - T = 2πr/v = 2π/ω (period)
 */
import { BaseSimulationEngine, DIMENSIONS, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'radius',
        name: 'Radius',
        symbol: 'r',
        unit: 'm',
        dimension: DIMENSIONS.LENGTH,
        min: 0.1,
        max: 10,
        default: 2,
        step: 0.1,
        description: 'Radius of circular path',
    },
    {
        id: 'mass',
        name: 'Mass',
        symbol: 'm',
        unit: 'kg',
        dimension: DIMENSIONS.MASS,
        min: 0.1,
        max: 100,
        default: 1,
        step: 0.1,
        description: 'Mass of the object',
    },
    {
        id: 'angularVelocity',
        name: 'Angular Velocity',
        symbol: '\\omega',
        unit: 'rad/s',
        dimension: DIMENSIONS.FREQUENCY,
        min: 0.1,
        max: 20,
        default: 2,
        step: 0.1,
        description: 'Rate of angular rotation',
    },
    {
        id: 'initialAngle',
        name: 'Initial Angle',
        symbol: '\\theta_0',
        unit: '°',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0,
        max: 360,
        default: 0,
        step: 15,
        description: 'Starting angular position',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'tangential_velocity',
        name: 'Tangential Velocity',
        latex: 'v = \\omega r',
        variables: ['tangentialVelocity', 'omega', 'radius'],
        description: 'Linear velocity tangent to the circle',
    },
    {
        id: 'centripetal_accel',
        name: 'Centripetal Acceleration',
        latex: 'a_c = \\frac{v^2}{r} = \\omega^2 r',
        variables: ['centripetalAccel', 'tangentialVelocity', 'radius', 'omega'],
        description: 'Acceleration directed toward center',
    },
    {
        id: 'centripetal_force',
        name: 'Centripetal Force',
        latex: 'F_c = \\frac{mv^2}{r} = m\\omega^2 r',
        variables: ['centripetalForce', 'mass', 'tangentialVelocity', 'radius'],
        description: 'Force required to maintain circular motion',
    },
    {
        id: 'period',
        name: 'Period',
        latex: 'T = \\frac{2\\pi r}{v} = \\frac{2\\pi}{\\omega}',
        variables: ['radius', 'tangentialVelocity', 'omega'],
        description: 'Time for one complete revolution',
    },
    {
        id: 'frequency',
        name: 'Frequency',
        latex: 'f = \\frac{\\omega}{2\\pi} = \\frac{1}{T}',
        variables: ['omega'],
        description: 'Number of revolutions per second',
    },
    {
        id: 'position_x',
        name: 'X Position',
        latex: 'x = r\\cos(\\theta)',
        variables: ['x', 'radius', 'theta'],
        description: 'Horizontal position',
    },
    {
        id: 'position_y',
        name: 'Y Position',
        latex: 'y = r\\sin(\\theta)',
        variables: ['y', 'radius', 'theta'],
        description: 'Vertical position',
    },
    {
        id: 'angular_position',
        name: 'Angular Position',
        latex: '\\theta(t) = \\theta_0 + \\omega t',
        variables: ['theta', 'initialAngle', 'omega'],
        description: 'Angle as function of time',
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'excessive_force',
        name: 'Excessive Centripetal Force',
        description: 'Required force exceeds practical limits',
        check: (state) => (state.variables['centripetalForce'] ?? 0) > 10000,
        severity: 'warning',
        educationalMessage: 'The required centripetal force is very high. ' +
            'In practice, this would require extremely strong materials or constraints.',
    },
    {
        id: 'relativistic_speed',
        name: 'Approaching Relativistic Speeds',
        description: 'Velocity is becoming relativistically significant',
        check: (state) => (state.variables['tangentialVelocity'] ?? 0) > 1e7,
        severity: 'warning',
        educationalMessage: 'The velocity is approaching relativistic speeds (>3% of light speed). ' +
            'Classical mechanics assumptions may not hold.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const CIRCULAR_MOTION_CONFIG = {
    id: 'circular-motion',
    slug: 'circular-motion',
    name: 'Circular Motion and Centripetal Force',
    domain: 'physics',
    description: 'Explore uniform circular motion',
    longDescription: `
Uniform circular motion occurs when an object moves in a circle at constant speed.
Despite constant speed, the velocity changes direction continuously, requiring acceleration.

Key concepts:
- Centripetal = "center-seeking"
- Acceleration is always toward the center
- Speed is constant, but velocity (vector) changes
- Force provides acceleration (F = ma applies)

Common examples:
- Satellite orbits
- Car on curved road
- Object on rotating platform
- Particle in magnetic field

Important relationships:
- Faster rotation → more force needed
- Larger radius at same ω → more force needed
- Period is independent of mass
  `.trim(),
    version: 1,
    conceptTags: ['circular-motion', 'centripetal', 'force', 'angular-velocity', 'period'],
    difficulty: 3,
    prerequisites: ['kinematics', 'forces', 'vectors'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'theta', type: 'parameter', parameterId: 'initialAngle' },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 1 / 120,
    maxDt: 1 / 30,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class CircularMotionSimulation extends BaseSimulationEngine {
    config = CIRCULAR_MOTION_CONFIG;
    constructor() {
        super({ theta: 0, omega: 2, x: 2, y: 0, tangentialVelocity: 0, centripetalAccel: 0, centripetalForce: 0 }, { type: 'euler', dt: 1 / 120, maxSubsteps: 10 });
    }
    getInitialState(params) {
        const r = params['radius'] ?? 2;
        const m = params['mass'] ?? 1;
        const omega = params['angularVelocity'] ?? 2;
        const theta0Deg = params['initialAngle'] ?? 0;
        const theta0 = theta0Deg * Math.PI / 180;
        const v = omega * r;
        const ac = omega * omega * r;
        const Fc = m * ac;
        return {
            theta: theta0,
            omega: omega,
            x: r * Math.cos(theta0),
            y: r * Math.sin(theta0),
            tangentialVelocity: v,
            centripetalAccel: ac,
            centripetalForce: Fc,
        };
    }
    computeDerivatives(_state, params) {
        const omega = params['angularVelocity'] ?? 2;
        return {
            theta: omega,
        };
    }
    integrate(dt) {
        const params = this.parameters;
        const r = params['radius'] ?? 2;
        const m = params['mass'] ?? 1;
        const omega = params['angularVelocity'] ?? 2;
        // Angular position advances uniformly
        this.state.theta += omega * dt;
        // Keep theta in [0, 2π)
        while (this.state.theta >= 2 * Math.PI) {
            this.state.theta -= 2 * Math.PI;
        }
        while (this.state.theta < 0) {
            this.state.theta += 2 * Math.PI;
        }
        // Update Cartesian coordinates
        this.state.x = r * Math.cos(this.state.theta);
        this.state.y = r * Math.sin(this.state.theta);
        // Update derived quantities
        this.state.omega = omega;
        this.state.tangentialVelocity = omega * r;
        this.state.centripetalAccel = omega * omega * r;
        this.state.centripetalForce = m * omega * omega * r;
    }
    computeEnergy(state, params) {
        const m = params['mass'] ?? 1;
        const v = state.tangentialVelocity;
        const kinetic = 0.5 * m * v * v;
        return {
            kinetic,
            potential: 0, // No height change in horizontal circle
            thermal: 0,
            total: kinetic,
        };
    }
    getPositionForStabilityCheck() {
        return this.state.theta;
    }
    /**
     * Get the period of rotation
     */
    getPeriod() {
        const omega = this.parameters['angularVelocity'] ?? 2;
        return (2 * Math.PI) / omega;
    }
    /**
     * Get velocity components
     */
    getVelocityComponents() {
        const v = this.state.tangentialVelocity;
        const theta = this.state.theta;
        // Velocity is perpendicular to radius (tangent)
        return {
            vx: -v * Math.sin(theta),
            vy: v * Math.cos(theta),
        };
    }
    /**
     * Get acceleration components (centripetal - toward center)
     */
    getAccelerationComponents() {
        const ac = this.state.centripetalAccel;
        const theta = this.state.theta;
        // Acceleration points toward center (negative of position direction)
        return {
            ax: -ac * Math.cos(theta),
            ay: -ac * Math.sin(theta),
        };
    }
}
/**
 * Factory function
 */
export function createCircularMotion() {
    return new CircularMotionSimulation();
}
//# sourceMappingURL=circular-motion.js.map